import React, { useContext, useEffect } from "react";
import { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Modal,
  Box,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { CepService } from "../../Services/CepService";
import { AppProvider } from "../../ContextApi/AppProvider";
import ImprimirOrcamento from "./ImprimirOrcamento";
import { pdf } from "@react-pdf/renderer";
import { ClienteService } from "../../Services/ClienteService";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import IProduto from "../Shared/Interfaces/Produto";
import { ProdutosServices } from "../../Services/ProdutosService";
import type { KitResponseDto } from "../Shared/Interfaces/Kit";
import OrcamentoModalResumo from "./OrcamentoModalResumo";
import ItensPedido from "../Shared/Interfaces/ItensPedido";
import {
  formatToBRL,
  limparMascaraDocumento,
  cpfApplyMask,
  cnpjApplyMask,
} from "../../Utils/utils";
import {
  Cliente,
  FormPesquisarCliente,
  initialStateCliente,
} from "../Shared/Interfaces/Cliente";
import Pedido, { FiltroPesquisarPedido } from "../Shared/Interfaces/Pedido";
import { useNavigate, useParams } from "react-router-dom";
import DefaultLoader from "../Shared/DefaultLoader";
import IPagamentosPedido from "../Shared/Interfaces/PagamentosPedido";
import { OrcamentoService } from "../../Services/OrcamentoService";
import colors from "../../theme/colors";
import { KitsService } from "../../Services/KitsService";

const cepService = new CepService();
const clienteService = new ClienteService();
const produtoService = new ProdutosServices();
const kitsService = new KitsService();
const orcamentoService = new OrcamentoService();

const initialStateOrcamento: Pedido = {
  id: 0,
  valorTotal: 0,
  quantidadeParcelas: 0,
  dataCriacao: "",
  observacoes: "",
  formaPagamento: "",
  cliente: initialStateCliente,
  itensPedido: [],
  pagamentosPedido: [],
};

const styleModal = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  width: { xs: "95vw", sm: 750 },
  height: "auto",
  transform: "translate(-50%, -50%)",
  maxWidth: "90vw",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
};

/** Número do endereço na API é int?; string vazia quebra a desserialização — envia null. */
function clienteNumeroParaApi(
  value: string | number | null | undefined
): number | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (s === "") return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

export default function GerarOrcamento() {
  const navigate = useNavigate();
  const { orcamentoId } = useParams();
  const pedidoIdNumber = Number(orcamentoId);

  /* Controles da modal de resumo */
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = () => {
    const dataAtual = new Date();
    cliente.documento = limparMascaraDocumento(cliente.documento);

    // Recalcula o valor total baseado em quantidade * preco para garantir consistência
    const valorTotalCalculado = itensPedido.reduce((acc, item) => acc + (item.quantidade * item.preco), 0);

    const pedidoModal: Pedido = {
      id: 0,
      valorTotal: valorTotalCalculado,
      quantidadeParcelas: 0,
      formaPagamento: "",
      observacoes: pedido.observacoes,
      dataCriacao: dataAtual.toISOString(),
      cliente: cliente,
      itensPedido: itensPedido,
      pagamentosPedido: pagamentosPedido,
    };

    setPedido(pedidoModal);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const contextApi = useContext(AppProvider);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [produtos, setProdutos] = React.useState<IProduto[]>([]);
  const [kits, setKits] = React.useState<KitResponseDto[]>([]);
  const [itensPedido, setItensPedido] = React.useState<ItensPedido[]>([]);
  const [pagamentosPedido, setPagamentosPedido] = React.useState<
    IPagamentosPedido[]
  >([]);
  const [pedido, setPedido] = React.useState<Pedido>(initialStateOrcamento);
  const [cliente, setCliente] = React.useState<Cliente>(initialStateCliente);
  const [clienteDocumento, setClienteDocumento] = React.useState("");
  const [clienteNome, setClienteNome] = React.useState("");
  const [produtoSelecionado, setProdutoSelecionado] = useState<IProduto>();
  const [kitSelecionado, setKitSelecionado] = useState<KitResponseDto>();
  const [quantidadeProduto, setQuantidadeProduto] = useState(0);

  type ProdutoKitBuscaResponse = {
    produtos: IProduto[];
    kits: KitResponseDto[];
  };

  type ProdutoKitOption =
    | { kind: "PRODUTO"; id: number; label: string; produto: IProduto }
    | { kind: "KIT"; id: number; label: string; kit: KitResponseDto };

  const [produtoBusca, setProdutoBusca] = useState("");
  const [produtoOptions, setProdutoOptions] = useState<ProdutoKitOption[]>([]);
  const [loadingProdutoBusca, setLoadingProdutoBusca] = useState(false);
  const [produtoOptionSelecionada, setProdutoOptionSelecionada] =
    useState<ProdutoKitOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [clientesEncontrados, setClientesEncontrados] = useState<Cliente[]>([]);
  const [selectedClients, setSelectedClients] = useState<Cliente[]>([]); // Armazena clientes selecionados

  // --- Edição inline do preço unitário (mesmo padrão da tela de pedidos) ---
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState<string>("");

  const moneyDigitsToBRL = (digits: string) => {
    const cents = digits ? parseInt(digits, 10) : 0;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  };

  const moneyStringToNumber = (value: string) => {
    const digits = value.replace(/\D/g, "");
    const cents = digits ? parseInt(digits, 10) : 0;
    return cents / 100;
  };

  const startEditPrice = (idProduto: number) => {
    const item = itensPedido.find((i) => i.idProduto === idProduto);
    if (!item) return;
    setEditingPriceId(idProduto);
    const cents = Math.round((item.preco ?? 0) * 100).toString();
    setEditingPriceValue(moneyDigitsToBRL(cents));
  };

  const cancelEditPrice = () => {
    setEditingPriceId(null);
    setEditingPriceValue("");
  };

  const saveEditPrice = (idProduto: number) => {
    const parsed = moneyStringToNumber(editingPriceValue);

    setItensPedido((prev) =>
      prev.map((item) =>
        item.idProduto === idProduto
          ? {
              ...item,
              preco: parsed,
              valorTotal: parsed * (item.quantidade || 0),
            }
          : item
      )
    );

    setEditingPriceId(null);
    setEditingPriceValue("");
  };

  const handleClienteDocumentoChange = (value: string) => {
    const onlyNumbers = limparMascaraDocumento(value);

    if (onlyNumbers.length === 11) {
      const cpf = cpfApplyMask(onlyNumbers);
      return setClienteDocumento(cpf);
    }

    if (onlyNumbers.length === 14) {
      const cnpj = cnpjApplyMask(onlyNumbers);
      return setClienteDocumento(cnpj);
    }

    setClienteDocumento(onlyNumbers);
  };

  const setClienteNomeChange = (value: string) => {
    setClienteNome(value);
    cliente.nome = value;
  };

    const handlePastecep = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault(); // Prevent the default paste action
      const pastedData = e.clipboardData.getData('Text');
      const cleanedData = limparMascaraDocumento(pastedData);
      handleInputCliente("cep", cleanedData);
    };

  const handleInputCliente = (fieldName: keyof Cliente, value: string) => {
    setCliente((prevCliente) => ({
      ...prevCliente,
      [fieldName]: value,
    }));
  };

  const addItemPedido = (item: ItensPedido) => {
    setItensPedido((prevItens) => [...prevItens, item]);
    setQuantidadeProduto(0);
    setProdutoOptionSelecionada(null);
    setProdutoSelecionado(undefined);
    setKitSelecionado(undefined);
    setProdutoBusca("");
    setProdutoOptions([]);
  };

  const ConcluirPedido = (valorTotal: number, observacao: string) => {
    setLoading(true);

    const pedidoBody: Pedido = {
      ...pedido,
      observacoes: observacao,
      valorTotal,
      cliente: {
        ...pedido.cliente,
        numero: clienteNumeroParaApi(pedido.cliente.numero),
      },
    };

    /*
    if (pedidoBody.cliente.documento == "") {
      contextApi.showMessage(
        "Documento do cliente é obrigatório",
        true,
        "warning"
      );
      return;
    }
*/
    if (pedidoIdNumber > 0) {
      pedidoBody.id = pedidoIdNumber;
      orcamentoService
        .putOrcamento(`/api/Orcamento/atualizar/${orcamentoId}`, pedidoBody)
        .then((response) => {
          if (response.status == 204) {
            openPDFInNewTab(pedidoIdNumber);
            setOpenModal(false);
            setCliente(initialStateCliente);
            setPedido(initialStateOrcamento);
            setPagamentosPedido([]);
            setItensPedido([]);
            BuscarProdutos();
            contextApi.showMessage(
              `Orcamento #000${pedidoIdNumber} Atualizado com Sucesso!`,
              true,
              "success"
            );
            setLoading(false);
            navigate("../consultarorcamentos");
          }
        })
        .catch((error) => {
          contextApi.showMessage(error.response.data, true, "error");
          setLoading(false);
        });
    } else {
      orcamentoService
        .postOrcamento("/api/Orcamento/adicionar", pedidoBody)
        .then((response) => {
          if (response.status == 201) {
            let idPedido = response.data.id;
            openPDFInNewTab(idPedido);
            setOpenModal(false);
            setCliente(initialStateCliente);
            setClienteDocumento("");
            setClienteNome("");
            setPedido(initialStateOrcamento);
            setPagamentosPedido([]);
            setItensPedido([]);
            BuscarProdutos();
            contextApi.showMessage(
              `Orcamento #000${idPedido} gerado com Sucesso!`,
              true,
              "success"
            );
            setLoading(false);
          }
        })
        .catch((error) => {
          contextApi.showMessage(error.response.data, true, "error");
          setLoading(false);
        });
    }
  };

  const BuscarCep = () => {
    setLoadingCep(true);
    if (cliente.cep == "") {
      contextApi.showMessage("Informe um cep válido!", true, "error");
      setLoadingCep(false);
    } else {
      cepService
        .getCep(`https://viacep.com.br/ws/${cliente.cep}/json/`)
        .then((response) => {
          if (response.data.erro) {
            contextApi.showMessage("Cep não encontrado", true, "warning");
            setLoadingCep(false);
          }
          handleInputCliente("bairro", response.data.bairro);
          handleInputCliente("complemento", response.data.complemento);
          handleInputCliente("cidade", response.data.localidade);
          handleInputCliente("logradouro", response.data.logradouro);
          handleInputCliente("estado", response.data.uf);
          setLoadingCep(false);
        })
        .catch((error) => {
          contextApi.showMessage(error.data.erro, true, "error");
          setLoadingCep(false);
        });
    }
  };

  const handleDelete = (idProduto: number) => {
    const updatedItensPedido = itensPedido.filter(
      (item) => item.idProduto !== idProduto
    );
    setItensPedido(updatedItensPedido);
  };

  const BuscarClientePorDocumento = () => {
    setLoadingCliente(true);
    setCliente(initialStateCliente);
    setSelectedClients([]);
    const filtroPesquisa: FormPesquisarCliente = {
      nome: clienteNome,
      documento: limparMascaraDocumento(clienteDocumento),
    };
    if (clienteDocumento == "" && clienteNome == "") {
      setLoadingCliente(false);
      contextApi.showMessage(
        "Informe um documento ou nome válido!",
        true,
        "error"
      );
    } else {
      clienteService
        .buscarPor(filtroPesquisa)
        .then((response) => {
          if (response.data.length === 0) {
            contextApi.showMessage("Nenhum cliente encontrado!", true, "info");
          } else if (response.data.length === 1) {
            setCliente(response.data[0]);
            handleClienteDocumentoChange(response.data[0].documento);
            setClienteNome(response.data[0].nome);
            setLoadingCliente(false);
          } else {
            setLoadingCliente(false);
            setClientesEncontrados(response.data);
            setIsModalOpen(true); // Abrindo a modal para seleção
          }
        })
        .catch((error) => {
          handleInputCliente("documento", clienteDocumento);
          handleInputCliente("nome", clienteNome);
          contextApi.showMessage(error.response.data, true, "error");
          setLoadingCliente(false);
        });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const openPDFInNewTab = async (idPedido: number) => {
    const blob = await pdf(
      <ImprimirOrcamento data={pedido} numeroPedido={`#000${idPedido}`} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const adicionarProduto = () => {
    if ((produtoSelecionado == null && kitSelecionado == null) || quantidadeProduto == 0) {
      contextApi.showMessage(
        "Informe o Produto e a Quantidade Corretamente!",
        true,
        "warning"
      );
      return;
    }

    const isKit = kitSelecionado != null;

    if (isKit) {
      const kitId = kitSelecionado?.id;
      if (
        kitId != null &&
        itensPedido.some((item) => (item.idKit ?? 0) === kitId)
      ) {
        contextApi.showMessage("Item já adicionado!", true, "warning");
        return;
      }
    } else {
      const pid = produtoSelecionado?.id;
      if (pid != null && itensPedido.some((item) => item.idProduto === pid)) {
        contextApi.showMessage("Item já adicionado!", true, "warning");
        return;
      }
    }
    const valor = isKit ? Number(kitSelecionado?.valor) || 0 : (produtoSelecionado?.valor ?? 0);
    const descricao = isKit
      ? `[KIT] ${kitSelecionado?.nome}`
      : `${produtoSelecionado?.descricao} - ${produtoSelecionado?.medida} - ${produtoSelecionado?.marca}`;

    const newItem: ItensPedido = {
      idProduto: isKit ? 0 : (produtoSelecionado?.id ?? 0),
      idKit: isKit ? (kitSelecionado?.id ?? 0) : 0,
      codigo: isKit ? (kitSelecionado?.id ?? 0) : (produtoSelecionado?.codigo ?? 0),
      descricao: descricao,
      quantidade: quantidadeProduto,
      preco: valor,
      valorTotal: quantidadeProduto * valor,
    };

    addItemPedido(newItem);
  };
  interface Column {
    id: "idProduto" | "descricao" | "quantidade" | "preco" | "valorTotal";
    label: string;
    minWidth?: number;
    align?: "right";
    format?: (value: number) => string;
  }

  const columns: readonly Column[] = [
    { id: "idProduto", label: "Id", minWidth: 20 },
    { id: "descricao", label: "Produto", minWidth: 150 },
    { id: "quantidade", label: "Quantidade", minWidth: 50 },
    {
      id: "preco",
      label: "Preco",
      minWidth: 100,
      format: (value: number) => formatToBRL(value),
    },
    {
      id: "valorTotal",
      label: "Total",
      minWidth: 100,
      format: (value: number) => formatToBRL(value),
    },
  ];

  const BuscarProdutos = async () => {
    try {
      const [prod, kitsResp] = await Promise.all([
        produtoService.getAll("/api/Produtos/Buscar"),
        kitsService.buscar(),
      ]);
      setProdutos(prod.data || []);
      setKits(Array.isArray(kitsResp.data) ? kitsResp.data : []);
    } catch (e) {
      contextApi.showMessage("Error ao buscar produtos", true, "error");
    }
  };

  const BuscarProdutosPorDescricao = async (descricao: string) => {
    const d = (descricao || "").trim();
    if (d.length < 3) {
      setProdutos([]);
      setKits([]);
      setProdutoOptions([]);
      return;
    }

    setLoadingProdutoBusca(true);
    try {
      const qs = new URLSearchParams({ descricao: d });
      const resp = await produtoService.getAll(
        `/api/Produtos/BuscarPorDescricao?${qs.toString()}`
      );

      const data = (resp.data || {}) as ProdutoKitBuscaResponse;
      const produtosResp = Array.isArray(data.produtos) ? data.produtos : [];
      const kitsResp = Array.isArray(data.kits) ? data.kits : [];

      setProdutos(produtosResp);
      setKits(kitsResp);

      const options: ProdutoKitOption[] = [
        ...kitsResp.map((k) => ({
          kind: "KIT" as const,
          id: k.id,
          label: `[KIT] ${k.nome} - ${formatToBRL(Number(k.valor) || 0)}`,
          kit: k,
        })),
        ...produtosResp.map((p) => ({
          kind: "PRODUTO" as const,
          id: p.id,
          label: `${p.descricao} - ${p.medida} - ${p.marca} - Estoque: ${p.quantidade}`,
          produto: p,
        })),
      ];
      setProdutoOptions(options);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setProdutos([]);
        setKits([]);
        setProdutoOptions([]);
      } else {
        const data = error?.response?.data;
        const msg =
          typeof data === "string"
            ? data
            : data && typeof data === "object"
              ? data.message || JSON.stringify(data)
              : error?.message ||
                "Ocorreu um erro interno. Tente novamente mais tarde.";
        contextApi.showMessage(msg, true, "error");
        setProdutos([]);
        setKits([]);
        setProdutoOptions([]);
      }
    } finally {
      setLoadingProdutoBusca(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    BuscarProdutos();
  }, []);

  useEffect(() => {
    const d = (produtoBusca || "").trim();
    if (d.length < 3) {
      setLoadingProdutoBusca(false);
      setProdutoOptions([]);
      setProdutos([]);
      setKits([]);
      setProdutoSelecionado(undefined);
      setKitSelecionado(undefined);
      setProdutoOptionSelecionada(null);
      return;
    }

    const t = window.setTimeout(() => {
      BuscarProdutosPorDescricao(d);
    }, 300);

    return () => window.clearTimeout(t);
  }, [produtoBusca]);

  useEffect(() => {
    if (orcamentoId) {
      const filtroPesquisa: FiltroPesquisarPedido = {
        idPedido: parseInt(orcamentoId),
        nomeCliente: "",
        dataInicial: null,
        dataFinal: null,
      };
      orcamentoService
        .buscarPor(filtroPesquisa)
        .then((response) => {
          // Recalcula o valorTotal de cada item para garantir consistência
          const itensAtualizados = response.data[0].itensPedido.map((item: ItensPedido) => ({
            ...item,
            valorTotal: item.quantidade * item.preco,
          }));
          
          setPedido(response.data[0]);
          setCliente(response.data[0].cliente);
          setItensPedido(itensAtualizados);
          setClienteDocumento(response.data[0].cliente.documento);
          setClienteNome(response.data[0].cliente.nome);
          setPagamentosPedido(response.data[0].pagamentosPedido);
        })
        .catch((error) => {
          contextApi.showMessage("Error fetching orçamento data", true, "error");
        });
    }
  }, [orcamentoId]);

  // Recalcula o valor total sempre que os itens do pedido mudam
  useEffect(() => {
    const novoValorTotal = itensPedido.reduce((acc, item) => acc + (item.quantidade * item.preco), 0);
    setPedido((prevPedido) => ({
      ...prevPedido,
      valorTotal: novoValorTotal,
    }));
  }, [itensPedido]);

  const handleCheckboxChange = (cliente: Cliente, checked: boolean) => {
    if (checked) {
      // Adiciona o cliente à lista de selecionados
      setSelectedClients((prev) => [...prev, cliente]);
    } else {
      // Remove o cliente da lista de selecionados
      setSelectedClients((prev) => prev.filter((c) => c.id !== cliente.id));
    }
  };

  const handleConfirmSelection = () => {
    if (selectedClients.length > 1) {
      contextApi.showMessage(
        "Só é possível selecionar um cliente",
        true,
        "error"
      );
      return;
    }

    setCliente(selectedClients[0]);
    setClienteDocumento(selectedClients[0].documento);
    setClienteNome(selectedClients[0].nome);
    setIsModalOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Cabeçalho e Descrição */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                📋 {parseInt(orcamentoId || "0") !== 0 ? "Editar Orçamento" : "Novo Orçamento"}
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Criação e gestão de orçamentos para clientes
              </Typography>
            </Paper>
          </Grid>

          {/* Linha 2 - Dados do Cliente */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>👤 Dados do Cliente</h4>
              
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* CPF/CNPJ */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      name="documento"
                      label="CPF ou CNPJ"
                      size="small"
                      fullWidth
                      disabled={pedidoIdNumber > 0}
                      variant="outlined"
                      value={clienteDocumento}
                      onChange={(e) => handleClienteDocumentoChange(e.target.value)}
                    />
                  </Grid>

                  {/* Nome Cliente */}
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      required
                      name="nome"
                      label="Nome Cliente"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={clienteNome}
                      onChange={(e) => setClienteNomeChange(e.target.value)}
                    />
                  </Grid>

                  {/* Botão Buscar Cliente */}
                  <Grid item xs={12} sm={6} md={1}>
                    <Button
                      type="submit"
                      sx={{ height: '40px', width: "100%" }}
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={pedidoIdNumber > 0 || loadingCliente}
                      onClick={() => BuscarClientePorDocumento()}
                    >
                      <SearchIcon />
                      {loadingCliente && (
                        <DefaultLoader
                          height="2.5em"
                          width="2.5em"
                          colors={[
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                          ]}
                          textInfo="Consultando cliente"
                        />
                      )}
                    </Button>
                  </Grid>

                  {/* Telefone */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      required
                      name="telefone"
                      label="Telefone"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.telefone}
                      onChange={(e) => handleInputCliente("telefone", e.target.value)}
                    />
                  </Grid>

                  {/* E-mail */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      name="email"
                      label="E-mail"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.email}
                      onChange={(e) => handleInputCliente("email", e.target.value)}
                    />
                  </Grid>

                  {/* CEP */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      required
                      name="cep"
                      label="CEP"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.cep}
                      onChange={(e) => handleInputCliente("cep", e.target.value)}
                      onPaste={handlePastecep}
                    />
                  </Grid>

                  {/* Botão Buscar CEP */}
                  <Grid item xs={12} sm={6} md={1}>
                    <Button
                      sx={{ height: '40px', width: "100%" }}
                      type="submit"
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => BuscarCep()}
                    >
                      <SearchIcon />
                      {loadingCep && (
                        <DefaultLoader
                          height="2.5em"
                          width="2.5em"
                          colors={[
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                            "#1876D2",
                          ]}
                          textInfo="Consultando cep"
                        />
                      )}
                    </Button>
                  </Grid>

                  {/* Espaçamento */}
                  <Grid item xs={12} sm={6} md={9}></Grid>

                  {/* Logradouro */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      name="logradouro"
                      label="Logradouro"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.logradouro}
                      onChange={(e) =>
                        handleInputCliente("logradouro", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Número */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      required
                      name="numero"
                      label="Número"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.numero}
                      onChange={(e) =>
                        handleInputCliente("numero", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Complemento */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      name="complemento"
                      label="Complemento"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.complemento}
                      onChange={(e) =>
                        handleInputCliente("complemento", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Bairro */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      required
                      name="bairro"
                      label="Bairro"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.bairro}
                      onChange={(e) =>
                        handleInputCliente("bairro", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Cidade */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      name="cidade"
                      label="Cidade"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.cidade}
                      onChange={(e) =>
                        handleInputCliente("cidade", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Estado */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      required
                      name="estado"
                      label="Estado"
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={cliente.estado}
                      onChange={(e) =>
                        handleInputCliente("estado", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Observações */}
                  <Grid item xs={12}>
                    <TextField
                      name="observacoes"
                      label="Observações"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={pedido.observacoes}
                      onChange={(e) =>
                        setPedido((prevPedido) => ({
                          ...prevPedido,
                          observacoes: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* Linha 3 - Produtos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🛍️ Produtos do Orçamento</h4>
              
              <Grid container spacing={2}>
                {/* Seleção de Produto */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={produtoOptions}
                    loading={loadingProdutoBusca}
                    inputValue={produtoBusca}
                    onInputChange={(_, newInputValue) =>
                      setProdutoBusca(newInputValue)
                    }
                    value={produtoOptionSelecionada}
                    getOptionLabel={(opt) => opt.label}
                    isOptionEqualToValue={(a, b) =>
                      a.kind === b.kind && a.id === b.id
                    }
                    onChange={(_, opt) => {
                      setProdutoOptionSelecionada(opt || null);
                      if (!opt) {
                        setProdutoSelecionado(undefined);
                        setKitSelecionado(undefined);
                        return;
                      }
                      if (opt.kind === "KIT") {
                        setKitSelecionado(opt.kit);
                        setProdutoSelecionado(undefined);
                      } else {
                        setProdutoSelecionado(opt.produto);
                        setKitSelecionado(undefined);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Digite a descrição (mín. 3 caracteres)"
                        placeholder="Ex.: pneu"
                      />
                    )}
                  />
                </Grid>

                {/* Quantidade */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    name="quantidade"
                    label="Quantidade"
                    type="number"
                    size="small"
                    fullWidth
                    variant="outlined"
                    value={quantidadeProduto}
                    onChange={(e) =>
                      setQuantidadeProduto(parseInt(e.target.value) || 0)
                    }
                  />
                </Grid>

                {/* Botão Adicionar */}
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ height: '40px', width: "100%" }}
                    onClick={adicionarProduto}
                  >
                    Adicionar
                  </Button>
                </Grid>

                {/* Espaçamento */}
                <Grid item xs={12} sm={6} md={4}></Grid>
              </Grid>

              {/* Tabela de Itens */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0', overflowX: "auto" }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Id</TableCell>
                          <TableCell>Descrição</TableCell>
                          <TableCell>Quantidade</TableCell>
                          <TableCell>Preço Unitário</TableCell>
                          <TableCell>Valor Total</TableCell>
                          <TableCell align="center">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itensPedido.map((row) => {
                          return (
                            <TableRow key={row.idProduto}>
                              <TableCell>{row.idProduto}</TableCell>
                              <TableCell>{row.descricao}</TableCell>
                              <TableCell>{row.quantidade}</TableCell>
                              <TableCell>
                                {editingPriceId === row.idProduto ? (
                                  <>
                                    <TextField
                                      size="small"
                                      value={editingPriceValue}
                                      onChange={(e) =>
                                        setEditingPriceValue(
                                          moneyDigitsToBRL(
                                            e.target.value.replace(/\D/g, "")
                                          )
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          saveEditPrice(row.idProduto);
                                        }
                                        if (e.key === "Escape") {
                                          cancelEditPrice();
                                        }
                                      }}
                                      inputProps={{ inputMode: "numeric" }}
                                      sx={{ width: { xs: "100%", sm: 140 } }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => saveEditPrice(row.idProduto)}
                                      aria-label="Salvar preço"
                                    >
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={cancelEditPrice}
                                      aria-label="Cancelar edição"
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </>
                                ) : (
                                  <>
                                    <span>{formatToBRL(row.preco)}</span>
                                    <IconButton
                                      size="small"
                                      onClick={() => startEditPrice(row.idProduto)}
                                      sx={{ ml: 1 }}
                                      aria-label="Editar preço"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </TableCell>
                              <TableCell>{formatToBRL(row.valorTotal)}</TableCell>
                              <TableCell align="center">
                                <DeleteIcon 
                                  onClick={() => handleDelete(row.idProduto)}
                                  sx={{ cursor: 'pointer', color: '#d32f2f' }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {itensPedido.length > 0 && (
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 100]}
                      component="div"
                      count={produtos.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </Paper>
              </Grid>
            </Paper>
          </Grid>

          {/* Linha 4 - Botão de Ação */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ height: '48px', px: 4 }}
                  onClick={handleOpenModal}
                >
                  {parseInt(orcamentoId || "0") !== 0
                    ? "Concluir Edição"
                    : "Gerar Orçamento"}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Modal de Resumo */}
      <OrcamentoModalResumo
        open={openModal}
        onClose={handleCloseModal}
        onConcluir={ConcluirPedido}
        pedido={pedido}
        loading={loading}
      />

      {/* Modal de Seleção de Cliente */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={styleModal}>
          <Typography variant="h6" style={{ textAlign: "center" }}>
            Selecione um Cliente
          </Typography>
          <div>
            {clientesEncontrados.map((cliente) => (
              <div key={cliente.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={(event) =>
                        handleCheckboxChange(cliente, event.target.checked)
                      }
                    />
                  }
                  label={`${cliente.nome} - ${cliente.documento} - ${cliente.telefone} - ${cliente.email}`}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ display: "flex" }}
              onClick={() => setIsModalOpen(false)}
            >
              Fechar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ display: "flex", marginLeft: "auto" }}
              disabled={selectedClients.length === 0}
              onClick={handleConfirmSelection}
            >
              Confirmar Seleção
            </Button>
          </div>
        </Box>
      </Modal>
    </Box>
  );
}
